/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    COOKIE_AUTH_MIGRATION
 * Date:         2026-02-11
 * Modified By:  Claude
 *
 * Description:
 * Updated auth utilities to work with HTTP-only cookie authentication.
 * Added checkAuthStatus() for async auth verification and logoutUser() for logout.
 * Deprecated localStorage-based functions (kept for backward compatibility).
 *
 * Related Screens / Modules:
 * - server/routes/users.js (/auth/me, /logout)
 * - server/middlewares/auth.js
 */

import { User } from "../interfaces/User";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { apiUrl } from "../config/environment";

interface JWTPayload {
  _id: string;
  isAdmin: boolean;
  firstName: string;
  lastName: string;
  preferredCurrency?: string;
}

// Partial user info extracted from JWT token
export type UserInfoFromToken = Pick<User, "firstName" | "lastName" | "isAdmin"> & {
  _id: string;
  preferredCurrency?: string;
  // [2026-06-23] impersonation: set when an admin is "viewing as" this user (read-only)
  isImpersonating?: boolean;
  impersonatedBy?: string;
};

/**
 * [2026-02-11] cookie-auth Phase 3: Check authentication status via server
 * Makes a request to /auth/me which validates the HTTP-only cookie
 * @returns User info if authenticated, null otherwise
 */
export async function checkAuthStatus(): Promise<UserInfoFromToken | null> {
  try {
    const response = await axios.get(`${apiUrl}/api/users/auth/me`, {
      withCredentials: true,
    });
    if (response.data) {
      return response.data as UserInfoFromToken;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * [2026-02-11] Cookie auth: Logout user by clearing server cookie
 * Makes a request to /logout which clears the HTTP-only cookie
 */
export async function logoutUser(): Promise<void> {
  try {
    await axios.post(`${apiUrl}/api/users/logout`, {}, { withCredentials: true });
  } catch (error) {
    console.error("Logout API call failed:", error);
  }
  // Always clear localStorage regardless of API success
  clearToken();
}

/**
 * @deprecated Use checkAuthStatus() for cookie-based auth
 * Get the authentication token from localStorage
 * @returns The token string or null if not found
 */
export function getToken(): string | null {
  return localStorage.getItem("uavtoken");
}

/**
 * @deprecated Use logoutUser() for cookie-based auth
 * Clear the authentication token from localStorage
 * Used when logging out or when token becomes invalid
 */
export function clearToken(): void {
  localStorage.removeItem("uavtoken");
}

/**
 * @deprecated Cookies are sent automatically with withCredentials: true
 * Get axios-compatible auth headers
 * @returns Headers object with Authorization header (empty if no token - cookie handles auth)
 */
export function getAuthHeaders(): { headers: { Authorization: string } } {
  const token = getToken();
  // [2026-02-11] cookie-auth Phase 3: Don't throw - cookie handles auth now
  // Return empty header, cookie will be sent automatically with withCredentials: true
  return {
    headers: {
      Authorization: token || "",
    },
  };
}

/**
 * @deprecated Cookies are sent automatically with withCredentials: true
 * Get auth headers optionally (returns empty headers if no token)
 * Used for endpoints that work with or without authentication
 * @returns Headers object with Authorization header, or empty headers
 */
export function getAuthHeadersOptional(): { headers: { Authorization?: string } } {
  const token = getToken();
  return {
    headers: token ? { Authorization: token } : {},
  };
}

export function getUserInfoFromToken(): UserInfoFromToken | null {
  try {
    const token = getToken();
    if (!token) return null;

    // Decode the JWT token to extract user information
    const decoded = jwtDecode<JWTPayload>(token);

    return {
      _id: decoded._id,
      isAdmin: decoded.isAdmin,
      firstName: decoded.firstName,
      lastName: decoded.lastName,
      preferredCurrency: decoded.preferredCurrency,
    };
  } catch (error) {
    console.error("Error getting user info from token:", error);
    return null;
  }
}
